import { useRef, useState } from 'react'
import { OverlayPanel } from 'primereact/overlaypanel'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Button from './ui/Button'
import ConfirmDialog, { confirmDialog } from './ui/ConfirmDialog'
import { useToast } from './ui/ToastProvider'
import ReadSwitch from '../features/notifications/components/ReadSwitch'
import {
  isClickable,
  useNotificationNavigate,
} from '../features/notifications/hooks/useNotificationNavigate'
import {
  useMarkAllNotificationsRead,
  useNotifications,
} from '../features/notifications/hooks/useNotifications'
import { useUnreadCount } from '../features/notifications/hooks/useUnreadCount'
import type { Notification, NotificationTargetType } from '../features/notifications/types'
import { formatDateTime } from '../lib/datetime'

/** Panelde gösterilen bildirim sayısı (spec FR-010). Sunucuya `size` olarak gider. */
const PANEL_SIZE = 5

/** Rozette gösterilen en büyük sayı; üstü `99+` olur (spec FR-008). */
const BADGE_MAX = 99

/**
 * "Tümünü Okundu Yap" onay penceresinin grubu. **Grup zorunludur:** düğme Topbar'da kalıcı mount'ludur
 * ve grupsuz `confirmDialog()` tüm grupsuz `<ConfirmDialog>` örneklerine yayın yapar — grupsuz bir
 * örnek burada dursaydı, açık olan her sayfanın kendi onayıyla birlikte ikinci bir pencere açılırdı.
 */
const MARK_ALL_CONFIRM_GROUP = 'notif-panel-mark-all'

/**
 * Satır ikonu hedef türünden türetilir — sunucu ikon göndermez. Böylece mockup'ın `.nic` kutusu
 * anlamını korur: kullanıcı satıra bakınca nereye gideceğini sezer.
 */
const TARGET_ICON: Record<NotificationTargetType, string> = {
  ACTION: 'pi pi-check-square',
  PRINCIPLE_DECISION: 'pi pi-book',
  MEETING: 'pi pi-calendar',
  UNRESOLVED: 'pi pi-bell',
  NONE: 'pi pi-bell',
}

/**
 * Üst çubuk bildirim kontrolü (058) — kenarsız çan düğmesi + okunmamış sayı rozeti; tıklayınca en
 * güncel {@link PANEL_SIZE} bildirimi gösteren panel açılır.
 *
 * <p><b>Görünüm mockup'tan gelir:</b> `.topbar-ic .notifbtn` düğmesi, inline çan SVG'si,
 * `OverlayPanel` + `.notif-op` / `.notif-menu` / `.nrow` / `.nic` / `.ntext` / `.nz` / `.nfoot`
 * yapısı korunur. Mockup'a göre üç ekleme vardır: kırmızı noktanın (`.ndot`) yerini alan sayı rozeti
 * (`.nbadge`), satırın sağındaki eylem kolonu (`.nact` — okundu anahtarı + "Detaya git") ve iki
 * eyleme bölünmüş alt çubuk (`.nfoot-bar`).
 *
 * <p><b>Satır artık kendisi tıklanmaz.</b> Yönlendirme yalnız "Detaya git" düğmesiyle yapılır: satırın
 * tamamı tıklanabilirken içine bir anahtar koymak (a) iç içe etkileşimli öğe üretir — geçersiz HTML ve
 * karışık klavye sırası — ve (b) "okundu yapayım" derken sayfa değiştirme kazasına yol açardı.
 *
 * <p>Dış-tıkla/ESC ile kapanma `OverlayPanel`'den gelir ve **hiçbir kaydı okundu işaretlemez**.
 *
 * <p><b>Alt çubukta iki eylem:</b> solda "Tümünü Gör", sağda "Tümünü Okundu Yap". Toplu işaretleme Bildirimler
 * sayfasındaki eylemle aynı uçtur ve aynı kurallara uyar: okunmamış yoksa pasif (spec FR-035), etkilenecek
 * kayıt sayısını söyleyen onaydan geçer (FR-034 — panel yalnız 5 satır gösterirken tamamı işaretlenir).
 */
export default function NotificationsButton() {
  const op = useRef<OverlayPanel>(null)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const markAllRead = useMarkAllNotificationsRead()

  // Panel kapalıyken liste YOKLANMAZ (bkz. useNotifications → options.poll): düğme Topbar'da kalıcı
  // mount'ludur, kapalı panelin arka planda tazelenmesinin görünür bir karşılığı yoktur.
  const [open, setOpen] = useState(false)

  const unread = useUnreadCount()
  const list = useNotifications({ page: 0, size: PANEL_SIZE, status: 'ALL' }, { poll: open })

  // Yönlendirme gerçekleştiğinde panel kapanır (spec FR-023).
  const openNotification = useNotificationNavigate(() => op.current?.hide())

  const count = unread.data?.count ?? 0
  const items = list.data?.content ?? []

  const openPanel = (event: React.MouseEvent) => {
    // Panel her açılışta tazelenir (spec FR-009.d): sayaç ve liste birlikte güncellenir, böylece
    // rozet ile panel içeriği arasında kalıcı bir tutarsızlık oluşmaz.
    unread.refetch()
    list.refetch()
    op.current?.toggle(event)
  }

  const viewAll = () => {
    op.current?.hide()
    navigate('/notifications')
  }

  /**
   * Panel onaydan ÖNCE kapanır: açık kalsa da onay penceresine yapılan ilk tıklama onu "dış tıklama"
   * olarak kapatırdı; önceden kapatmak maskenin arkasında yarım kalmış bir panel bırakmaz.
   */
  const confirmMarkAllRead = () => {
    op.current?.hide()
    confirmDialog({
      group: MARK_ALL_CONFIRM_GROUP,
      header: t('notifications.markAllRead.title'),
      message: t('notifications.markAllRead.panelMessage', { count }),
      acceptLabel: t('notifications.markAllRead.accept'),
      accept: () => {
        markAllRead.mutate(undefined, {
          onSuccess: (result) => {
            toast.show({
              severity: 'success',
              summary: t('notifications.markAllRead.done', { count: result.updatedCount }),
              life: 3000,
            })
          },
        })
      },
    })
  }

  return (
    <>
      <ConfirmDialog group={MARK_ALL_CONFIRM_GROUP} />
      <button
        type="button"
        className="topbar-ic notifbtn"
        aria-label={
          count > 0 ? `${t('shell.notifications')} (${t('shell.notificationsUnreadAria', { count })})` : t('shell.notifications')
        }
        onClick={openPanel}
      >
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M10 3a4 4 0 0 0-4 4c0 3.5-1.3 4.7-1.3 4.7h10.6S14 10.5 14 7a4 4 0 0 0-4-4z" />
          <path d="M8.5 15a1.5 1.5 0 0 0 3 0" />
        </svg>
        {/* Sıfırsa rozet HİÇ çizilmez — "0" yazan bir rozet gösterilmez (spec FR-007). */}
        {count > 0 && (
          <span className="nbadge" aria-hidden="true">
            {count > BADGE_MAX ? `${BADGE_MAX}+` : count}
          </span>
        )}
      </button>

      <OverlayPanel
        ref={op}
        className="notif-op"
        onShow={() => setOpen(true)}
        onHide={() => setOpen(false)}
      >
        <div className="notif-menu">
          <div className="pmh">{t('shell.notifications')}</div>

          {list.isPending ? (
            <div className="notif-empty-state">
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
              <div>{t('notifications.loading')}</div>
            </div>
          ) : items.length === 0 ? (
            <div className="notif-empty-state">
              <i className="pi pi-bell-slash" aria-hidden="true" />
              <div>{t('shell.notificationsEmpty')}</div>
            </div>
          ) : (
            items.map((item) => (
              <NotificationRow key={item.id} item={item} onOpen={openNotification} />
            ))
          )}

          {/* İki eylem de her durumda görünür: boş listede de kullanıcı geçmişe gidebilmelidir;
              "Tümünü Okundu Yap" okunmamış yoksa gizlenmez, pasifleşir (spec FR-035). */}
          <div className="nfoot-bar">
            <button type="button" className="nfoot" onClick={viewAll}>
              {t('shell.notificationsViewAll')}
            </button>
            <button
              type="button"
              className="nfoot"
              onClick={confirmMarkAllRead}
              disabled={count === 0 || markAllRead.isPending}
            >
              {t('shell.notificationsMarkAllRead')}
            </button>
          </div>
        </div>
      </OverlayPanel>
    </>
  )
}

/**
 * Tek bildirim satırı. Okunmamışlar `.nrow.unread` ile kalın/vurgulu görünür (spec FR-013).
 *
 * <p><b>İki satırlık kompakt düzen:</b> başlık üstte, içerik ve tarih **aynı** alt satırda
 * (`.nsub`). Üç metnin alt alta dizilmesi kartı gereksiz uzatıyordu; her iki metin de tek satıra
 * kırpılır (`.nclip`), böylece uzun içerik kartı büyütemez ve panel sabit yükseklikte kalır.
 *
 * <p>Sağdaki eylem kolonu (`.nact`): okundu/okunmadı anahtarı ve — hedefi varsa — "Detaya git".
 * Hedefsiz satırda (`NONE`) yalnız anahtar görünür (spec FR-020.5): gidilecek yer yoktur.
 */
function NotificationRow({
  item,
  onOpen,
}: Readonly<{
  item: Notification
  onOpen: (item: Notification) => void
}>) {
  const { t } = useTranslation()

  const date = formatDateTime(item.plannedSendingTime)

  return (
    <div className={item.read ? 'nrow' : 'nrow unread'}>
      <span className="nic">
        <i className={TARGET_ICON[item.targetType]} aria-hidden="true" />
      </span>
      <div className="ntext">
        {/* Her iki dilde de başlık boşsa yedek etiket gösterilir; satır bozulmaz (spec FR-014). */}
        <h5 className="nclip">{item.subject ?? t('notifications.untitled')}</h5>
        <div className="nsub">
          {item.content && <p className="nclip">{item.content}</p>}
          <span className="nz">{date}</span>
        </div>
      </div>
      <div className="nact">
        <ReadSwitch item={item} />
        {isClickable(item.targetType) && (
          <Button
            className="nact-go"
            icon="pi pi-arrow-right"
            rounded
            text
            aria-label={t('notifications.openDetail')}
            tooltip={t('notifications.openDetail')}
            tooltipOptions={{ position: 'left' }}
            onClick={() => onOpen(item)}
          />
        )}
      </div>
    </div>
  )
}
